import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@apollo/client';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { Mail } from 'lucide-react';

import { GET_ME } from '../../graphql/queries';

const API_URL = import.meta.env.VITE_GRAPHQL_URL.replace('/graphql', '');
const COOLDOWN_SECONDS = 60;

/**
 * Signing in doesn't wait on the email (the API hands out a JWT at signup): until the link is clicked, every app
 * page carries this bar. It can't be dismissed and goes once `me.confirmed` comes back true. A guided session
 * (/execute) is left alone, mid-workout
 */
export const EmailConfirmBanner = () => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const { data, refetch } = useQuery(GET_ME);
  const [isSending, setIsSending] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const barRef = useRef(null);
  const copy = (key, options) => t(`components.EmailConfirmBanner.${key}`, options);

  const email = data?.me?.email;
  const isVisible = data?.me?.confirmed === false && !pathname.startsWith('/execute');

  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const timer = setTimeout(() => setCooldown(cooldown - 1), 1000);
    return () => clearTimeout(timer);
  }, [cooldown]);

  // Confirmed from the email in another tab: coming back here checks again
  useEffect(() => {
    if (!isVisible) return undefined;
    const onVisible = () => {
      if (document.visibilityState === 'visible') refetch();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [isVisible, refetch]);

  // Publishes the bar's height so the desktop tab bar (fixed at the top) sits below it, as AppBanner does
  useEffect(() => {
    const root = document.documentElement;
    const bar = barRef.current;
    if (!isVisible || !bar) {
      root.style.removeProperty('--mt-confirm-h');
      return undefined;
    }
    const publish = () => root.style.setProperty('--mt-confirm-h', `${bar.offsetHeight}px`);
    publish();
    const observer = typeof ResizeObserver === 'function' ? new ResizeObserver(publish) : null;
    observer?.observe(bar);
    return () => {
      observer?.disconnect();
      root.style.removeProperty('--mt-confirm-h');
    };
  }, [isVisible]);

  if (!isVisible) return null;

  const resend = async () => {
    setIsSending(true);
    try {
      const response = await fetch(`${API_URL}/api/auth/resend-confirmation`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      toast.success(t('notifications.resendConfirmationSuccess'));
      setCooldown(COOLDOWN_SECONDS);
    } catch {
      toast.error(t('notifications.resendConfirmationError'));
    } finally {
      setIsSending(false);
    }
  };

  let resendLabel = copy('resend');
  if (isSending) resendLabel = copy('sending');
  else if (cooldown > 0) resendLabel = copy('cooldown', { seconds: cooldown });

  return (
    <div ref={barRef} className="mt-root mt-confirm-bar" role="status">
      <Mail className="mt-confirm-bar__icon" aria-hidden="true" />
      <p className="mt-confirm-bar__text">
        <strong>{copy('title')}</strong> {copy('sentTo', { email })}
      </p>
      <button
        type="button"
        className="mt-confirm-bar__resend"
        onClick={resend}
        disabled={isSending || cooldown > 0}
      >
        {resendLabel}
      </button>
    </div>
  );
};
